/* SimpleShot — Shipping cutoff countdown
 * Renders into <span class="shipping-estimate"></span> elements on PDP.
 *
 * Config is injected inline (no network request) as JSON in
 *   <script type="application/json" id="ss-shipping-config">…</script>
 * rendered once per page from the shop metafield custom.shipping_config.
 * Shape:
 *   { "cutoff_hour": 9,
 *     "exceptions": [ { "date":"2026-09-07", "no_ship":true, "note":"Labor Day" },
 *                     { "date":"2026-11-28", "cutoff_hour":7,  "note":"half day" } ] }
 *   - cutoff_hour        : default daily cutoff (ET, 24h). Missing → 9.
 *   - exceptions[].date  : ISO YYYY-MM-DD (ET calendar date).
 *   - exceptions[].no_ship      : true → warehouse closed, skip this date entirely.
 *   - exceptions[].cutoff_hour  : per-date cutoff override (half days).
 *   - exceptions[].message      : replaces the whole "Ships …" string on THAT
 *                                 date. For days the one-exact-ship-day model
 *                                 cannot describe honestly — a closure, or a
 *                                 backlog where the next open day is already
 *                                 oversubscribed. Plain text, no countdown.
 *   - exceptions[].message_note : optional SECOND line, rendered under the
 *                                 badge in .ss-ship-note. Use it for the part
 *                                 that will not fit on the badge line.
 *   - exceptions[].message_note_from_hour : optional ET hour (0-23). The note
 *                                 stays hidden until that hour, then appears.
 *                                 Use it when the explanation only becomes
 *                                 relevant once the day's cutoff has passed —
 *                                 e.g. a closure two days out matters to the
 *                                 afternoon shopper, not the 7am one whose
 *                                 order ships today regardless.
 *   - exceptions[].message_short: shorter variant used only in the mobile
 *                                 sticky add-to-cart bar (single-line layout,
 *                                 ~42 chars before it wraps). Falls back to
 *                                 `message` when absent.
 *
 * Previewing a future date: on any UNPUBLISHED theme, ?ss_ship_now=<ISO 8601>
 * overrides "now" for that pageview, so a closure or message day can be seen in
 * the real layout before it arrives. Inert on the published theme.
 *
 * Logic: walk forward from today (ET) to the next valid ship day — skip Sat/Sun
 * and no_ship dates, honor per-date cutoff overrides. Hard 30-day walk cap as a
 * malformed-config safety net. Defaults (9am, no exceptions) are baked in so the
 * PDP never breaks if the metafield is missing or unparseable.
 *
 * Wins vs the prior version:
 *   - Real ET calendar math; DST-correct target instant (two-pass Intl offset).
 *     No fixed 86,400,000-ms day arithmetic — the old countdown drifted an hour
 *     across DST transitions.
 *   - Data-driven cutoff + holiday/half-day exceptions (metafield, not hardcoded).
 *   - Pauses entirely when tab is hidden, refreshes on visibility return.
 *   - Skips DOM writes when text hasn't changed.
 *   - Self-scheduling timer: 60s cadence in countdown mode, single timeout to
 *     "enter countdown" when in "Ships [day]" mode (no recurring work).
 *   - Cacheable as a separate asset, deferred load.
 */
(function () {
  'use strict';

  if (!document.querySelector('.shipping-estimate')) return;

  var DEFAULT_CUTOFF_HOUR = 9;
  var WALK_CAP_DAYS = 30; // malformed-config safety net
  var DAYS_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var MONTHS_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var WEEKDAY_INDEX = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

  // One-time formatter — Intl is expensive to construct, cheap to reuse.
  var ET_FORMATTER = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false, weekday: 'short'
  });

  // ---- Config -------------------------------------------------------------

  function loadConfig() {
    var cutoffHour = DEFAULT_CUTOFF_HOUR;
    var exceptions = {}; // keyed by ISO date → { no_ship, cutoff_hour }

    var el = document.getElementById('ss-shipping-config');
    if (el && el.textContent) {
      try {
        var raw = JSON.parse(el.textContent);
        if (raw && typeof raw.cutoff_hour === 'number' &&
            raw.cutoff_hour >= 0 && raw.cutoff_hour <= 23) {
          cutoffHour = raw.cutoff_hour;
        }
        if (raw && Array.isArray(raw.exceptions)) {
          for (var i = 0; i < raw.exceptions.length; i++) {
            var e = raw.exceptions[i];
            if (!e || typeof e.date !== 'string') continue;
            var entry = {};
            if (e.no_ship === true) entry.no_ship = true;
            if (typeof e.cutoff_hour === 'number' &&
                e.cutoff_hour >= 0 && e.cutoff_hour <= 23) {
              entry.cutoff_hour = e.cutoff_hour;
            }
            if (typeof e.message === 'string' && e.message) {
              entry.message = e.message;
            }
            if (typeof e.message_short === 'string' && e.message_short) {
              entry.message_short = e.message_short;
            }
            if (typeof e.message_note === 'string' && e.message_note) {
              entry.message_note = e.message_note;
            }
            if (typeof e.message_note_from_hour === 'number' &&
                e.message_note_from_hour >= 0 && e.message_note_from_hour <= 23) {
              entry.message_note_from_hour = e.message_note_from_hour;
            }
            exceptions[e.date] = entry;
          }
        }
      } catch (err) {
        // Malformed config → fall back to baked defaults, never break the PDP.
      }
    }
    return { cutoffHour: cutoffHour, exceptions: exceptions };
  }

  var CONFIG = loadConfig();

  // ---- Clock --------------------------------------------------------------

  // Test-only time travel. On any UNPUBLISHED theme, ?ss_ship_now=<ISO 8601>
  // shifts "now" for that pageview so a future closure or message day renders
  // in the real layout. Gated on Shopify.theme.role, so it is inert on the
  // published theme; also inert if the value does not parse.
  // "Now" comes from Shopify's clock, written into the page when it was built
  // (data-server-now on the .shipping-estimate spans). The shopper's clock is
  // never read; elapsed time since load is added with performance.now().
  var SERVER_BASE = NaN, PERF0 = 0;
  (function () {
    var el = document.querySelector('[data-server-now]');
    if (!el) return;
    var v = parseInt(el.getAttribute('data-server-now'), 10);
    if (isFinite(v)) { SERVER_BASE = v * 1000; PERF0 = performance.now(); }
  })();

  var CLOCK_OFFSET = 0;
  (function () {
    try {
      var role = window.Shopify && Shopify.theme && Shopify.theme.role;
      if (!role || String(role).toLowerCase() === 'main') return;
      var m = /[?&]ss_ship_now=([^&]+)/.exec(window.location.search);
      if (!m) return;
      var t = Date.parse(decodeURIComponent(m[1]));
      if (isFinite(t) && isFinite(SERVER_BASE)) CLOCK_OFFSET = t - SERVER_BASE;
    } catch (e) { /* no override */ }
  })();

  function clockNow() { return SERVER_BASE + (performance.now() - PERF0) + CLOCK_OFFSET; }

  // ---- ET date math -------------------------------------------------------

  // Current ET wall-clock parts.
  function getET() {
    if (!isFinite(clockNow())) return null;
    var parts = ET_FORMATTER.formatToParts(new Date(clockNow()));
    var p = {};
    parts.forEach(function (part) { p[part.type] = part.value; });
    return {
      year: parseInt(p.year, 10),
      month: parseInt(p.month, 10),
      day: parseInt(p.day, 10),
      hour: parseInt(p.hour, 10) % 24, // 'hour12:false' yields "24" at midnight in some locales
      minute: parseInt(p.minute, 10),
      second: parseInt(p.second, 10),
      dayOfWeek: WEEKDAY_INDEX[p.weekday]
    };
  }

  // ET offset from UTC (in ms, negative west of UTC) at a given instant.
  function etOffsetAt(ts) {
    // Guard (2026-08-03): formatToParts throws RangeError "Invalid time value" on a
    // NaN date. A single bad parse upstream used to take the whole script down.
    if (!isFinite(ts)) return 0;
    var parts = ET_FORMATTER.formatToParts(new Date(ts));
    var p = {};
    parts.forEach(function (part) { p[part.type] = part.value; });
    var asUTC = Date.UTC(
      parseInt(p.year, 10), parseInt(p.month, 10) - 1, parseInt(p.day, 10),
      parseInt(p.hour, 10) % 24, parseInt(p.minute, 10), parseInt(p.second, 10)
    );
    return asUTC - ts;
  }

  // Convert an ET wall-clock date+hour into a real epoch-ms instant, DST-correct.
  // Two passes so the offset is resolved even right at a DST boundary.
  function etWallToEpoch(y, mo, d, h) {
    var wallAsUTC = Date.UTC(y, mo - 1, d, h, 0, 0);
    var off = etOffsetAt(wallAsUTC);
    var ts = wallAsUTC - off;
    var off2 = etOffsetAt(ts);
    if (off2 !== off) ts = wallAsUTC - off2;
    return ts;
  }

  function iso(y, mo, d) {
    var mm = mo < 10 ? '0' + mo : '' + mo;
    var dd = d < 10 ? '0' + d : '' + d;
    return y + '-' + mm + '-' + dd;
  }

  // Walk forward from today (ET) to the next valid ship day whose cutoff is
  // still in the future. Returns the ship date + its cutoff instant, or null
  // if nothing valid within the 30-day cap (malformed config).
  function nextShipDay() {
    var nowMs = clockNow();
    var et = getET();
    // Guard (2026-08-03): some browsers/locales return unexpected part names from
    // formatToParts, so these parse to NaN. Bail out and leave the badge alone
    // rather than building an Invalid Date and throwing downstream.
    if (!et || !isFinite(et.year) || !isFinite(et.month) || !isFinite(et.day)) return null;
    // Iterate ET calendar dates using a UTC anchor at noon (safe from DST edges).
    var cur = new Date(Date.UTC(et.year, et.month - 1, et.day, 12, 0, 0));
    if (isNaN(cur.getTime())) return null;

    for (var i = 0; i < WALK_CAP_DAYS; i++) {
      var y = cur.getUTCFullYear();
      var mo = cur.getUTCMonth() + 1;
      var d = cur.getUTCDate();
      var dow = cur.getUTCDay();
      var exc = CONFIG.exceptions[iso(y, mo, d)];

      var isWeekend = (dow === 0 || dow === 6);
      var noShip = !!(exc && exc.no_ship);

      if (!isWeekend && !noShip) {
        var cutHour = (exc && typeof exc.cutoff_hour === 'number')
          ? exc.cutoff_hour : CONFIG.cutoffHour;
        var cutoffMs = etWallToEpoch(y, mo, d, cutHour);
        if (nowMs < cutoffMs) {
          return {
            year: y, month: mo, day: d, dayOfWeek: dow,
            cutoffMs: cutoffMs, daysFromToday: i
          };
        }
      }
      cur.setUTCDate(cur.getUTCDate() + 1);
    }
    return null;
  }

  // ---- Rendering ----------------------------------------------------------

  var timer = null;

  // Only touch the DOM if a span's text actually differs. Compared PER SPAN
  // rather than against one cached string, so a freshly re-rendered (empty)
  // span always gets filled — a variant change replaces the span wholesale,
  // and a single cached `lastText` would skip the write and leave it blank.
  //
  // The mobile sticky add-to-cart bar is a deliberately single-line layout
  // (13px, line-height 1), so spans inside it take the short text when one is
  // supplied; every other span takes the full text.
  function writeSpans(spans, text, shortText) {
    for (var i = 0; i < spans.length; i++) {
      var t = (shortText && spans[i].closest && spans[i].closest('.ss-sticky-ship'))
        ? shortText : text;
      if (spans[i].innerHTML !== t) spans[i].innerHTML = t;
    }
  }

  // The optional second line under the badge. Cleared on every normal day so
  // `.ss-ship-note:empty` collapses it.
  function writeNotes(text) {
    var notes = document.querySelectorAll('.ss-ship-note');
    for (var i = 0; i < notes.length; i++) {
      if (notes[i].innerHTML !== text) notes[i].innerHTML = text;
    }
  }

  // ms until the next ET midnight, floored at 60s and capped at 30 min so a
  // per-date message clears promptly once the date rolls over.
  function msUntilEtMidnight() {
    var et = getET();
    if (!et || !isFinite(et.hour)) return 600000;
    var msLeft = (86400 - (et.hour * 3600 + et.minute * 60 + et.second)) * 1000;
    return Math.max(60000, Math.min(msLeft, 1800000));
  }

  function buildText(ship, nowMs) {
    var msLeft = ship.cutoffMs - nowMs;
    var hoursLeft = msLeft / 3600000;

    var label;
    if (ship.daysFromToday === 0) {
      label = 'today';
    } else if (ship.daysFromToday === 1) {
      label = 'tomorrow';
    } else if (ship.daysFromToday > 6) {
      // More than 6 days out → include the date.
      label = DAYS_FULL[ship.dayOfWeek] + ', ' + MONTHS_ABBR[ship.month - 1] + ' ' + ship.day;
    } else {
      label = DAYS_FULL[ship.dayOfWeek];
    }

    // Countdown only within 24h of cutoff.
    if (hoursLeft <= 24) {
      var totalMin = Math.floor(msLeft / 60000);
      var hrs = Math.floor(totalMin / 60);
      var min = totalMin % 60;
      var countdown = hrs > 0
        ? hrs + ' hr' + (hrs !== 1 ? 's' : '') + ' ' + min + ' min'
        : min + ' min';
      return 'Ships ' + label + '. Order in <strong>' + countdown + '</strong>.';
    }
    return 'Ships ' + label + '.';
  }

  // Backstop (2026-08-03): a shipping badge must never be able to throw into the
  // page. Anything unexpected in date math leaves the badge as-is instead of
  // killing every script that runs after it. Internal setTimeout(update, …) calls
  // resolve to this safe wrapper, so retries stay guarded too.
  function update() {
    try { updateCore(); } catch (e) { /* leave the badge untouched */ }
  }

  function updateCore() {
    var spans = document.querySelectorAll('.shipping-estimate');
    if (!spans.length) return;

    var nowMs = clockNow();

    // Per-date message override. When TODAY carries a message, that text
    // replaces the computed "Ships …" string outright — no ship day, no
    // countdown. Keyed on today's ET date, not the ship date, so it shows on
    // the day it describes and clears itself at midnight.
    var etNow = getET();
    var todayExc = (etNow && isFinite(etNow.year))
      ? CONFIG.exceptions[iso(etNow.year, etNow.month, etNow.day)]
      : null;
    // `message_note` stands on its own: a date may carry ONLY a note, leaving
    // the badge line as the normal computed ship day with an explanation under
    // it (the backlog days after a closure).
    var note = '';
    if (todayExc && todayExc.message_note) {
      var fromHour = todayExc.message_note_from_hour;
      note = (typeof fromHour !== 'number' ||
              (etNow && isFinite(etNow.hour) && etNow.hour >= fromHour))
        ? todayExc.message_note : '';
    }

    if (todayExc && todayExc.message) {
      writeSpans(spans, todayExc.message, todayExc.message_short || todayExc.message);
      writeNotes(note);
      if (timer) { clearTimeout(timer); timer = null; }
      timer = setTimeout(update, msUntilEtMidnight());
      return;
    }

    var ship = nextShipDay();
    if (!ship) return; // malformed config safety net — leave spans untouched

    writeSpans(spans, buildText(ship, nowMs), null);
    writeNotes(note);

    // Schedule next run based on mode.
    if (timer) { clearTimeout(timer); timer = null; }

    var msLeft = ship.cutoffMs - nowMs;
    if (msLeft / 3600000 > 24) {
      // Static "Ships [day]" mode — single timeout to the moment we enter the
      // countdown window (msLeft === 24h). 60s safety floor against tiny drift.
      var msUntilCountdown = Math.max(60000, msLeft - 24 * 3600000);
      timer = setTimeout(update, msUntilCountdown);
    } else {
      // Active countdown — refresh every minute.
      timer = setTimeout(update, 60000);
    }
  }

  function pause() {
    if (timer) { clearTimeout(timer); timer = null; }
  }

  function resume() {
    // Force a recalc on resume — text may be stale.
    update();
  }

  // Page Visibility API — pause work entirely when tab is hidden.
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) pause(); else resume();
  });

  // Variant change — product-info.js does `#Price-<section>.innerHTML = …`,
  // which DESTROYS the whole .m-price node (and the .shipping-estimate span
  // inside it) and replaces it with freshly server-rendered, empty markup.
  // So observe the WRAPPER — #Price-… is only ever written into, never itself
  // replaced. An observer bound to .m-price is detached by the first variant
  // change, never fires again, and the badge stays blank for the rest of the
  // session. (Bug found 2026-07-21 on the Scout LT2 PDP.)
  var firstSpan = document.querySelector('.shipping-estimate');
  var priceWrapper = firstSpan && (
    firstSpan.closest('[id^="Price-"]') ||
    firstSpan.closest('.product-block-group--price') ||
    (firstSpan.closest('.m-price') || {}).parentElement
  );
  if (priceWrapper) {
    var debounceId = null;
    new MutationObserver(function () {
      // Debounced; update() diffs per span, so re-entry from our own write
      // finds nothing to change and settles immediately.
      if (debounceId) clearTimeout(debounceId);
      debounceId = setTimeout(update, 50); // low-stock state may have changed
    }).observe(priceWrapper, { childList: true, subtree: true, characterData: true });
  }

  // Belt-and-braces: product-info.js dispatches this after every variant
  // re-render, so the badge refills even if the wrapper selector ever moves.
  document.addEventListener('variant:changed', function () {
    setTimeout(update, 50);
  });

  // ---- Sticky bar: where the badge lives -----------------------------------
  // The badge rides on the price line inside the sticky bar's left block. The
  // moment that line has no room for it, it moves up to the standalone centered
  // line rather than wrapping under the price. CSS cannot make this call: it
  // depends on how wide a given product's price renders (a sale price plus its
  // struck-out compare-at is far wider than a plain one), which no media query
  // knows. A fixed breakpoint has to be set for the worst case, so it fires
  // early on everything else — that was the old 1120px cutoff.
  //
  // What is measured is the INTRINSIC width of the price line's children, not
  // whether it currently wraps. That gives the same answer whichever home the
  // badge is in, so toggling the class can't feed back into the measurement and
  // oscillate.
  function measureStickyBadgeFit() {
    var bar = document.querySelector('.m-sticky-addtocart');
    if (!bar) return;
    var price = bar.querySelector('.m-sticky-addtocart--title .m-price');
    if (!price) return;
    var badge = price.querySelector('.in-stock-badge');
    if (!badge) return;

    // .ss-measuring lays the badge out even when the wrapped state has hidden
    // it. Added and removed synchronously — no frame renders in between.
    bar.classList.add('ss-measuring');
    var avail = price.clientWidth;
    var needed = 0;
    for (var i = 0; i < price.children.length; i++) {
      var child = price.children[i];
      // The blurb is a full-width flex row of its own; it never competes for
      // horizontal space, so counting it would force a false "doesn't fit".
      if (child.className && String(child.className).indexOf('ss-ship-note') !== -1) continue;
      needed += child.getBoundingClientRect().width;
    }
    bar.classList.remove('ss-measuring');

    // 8px of slack for the flex gap and sub-pixel rounding.
    if (needed + 8 > avail) bar.classList.add('ss-badge-wrapped');
    else bar.classList.remove('ss-badge-wrapped');
  }

  function measureSafe() {
    try { measureStickyBadgeFit(); } catch (e) { /* never break the bar */ }
  }

  var resizeId = null;
  window.addEventListener('resize', function () {
    if (resizeId) clearTimeout(resizeId);
    resizeId = setTimeout(measureSafe, 120);
  });

  document.addEventListener('variant:changed', function () {
    setTimeout(measureSafe, 60);
  });

  // Kick off.
  update();
  measureSafe();
  // The sticky bar mounts after first paint on some templates; one late pass
  // catches it without polling.
  setTimeout(measureSafe, 600);
})();
